import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import type {
  TellentAssistantAppContextPriority,
  TellentAssistantAppContextWriter,
} from '@core/tellent-assistant-contracts';
import type { AppContextEntity, TellentAssistantAppContext } from '@core/tellent-assistant-types';

const PRIORITY_ORDER: Record<TellentAssistantAppContextPriority, number> = { page: 1, overlay: 2 };

interface ContextEntry {
  entity: AppContextEntity;
  priority: TellentAssistantAppContextPriority;
}

const entityKey = (entity: AppContextEntity): string => `${entity.type}:${entity.id}`;

/** Orders entities page-first, overlay-last; same type+id collapses to the latest push. */
@Injectable({ providedIn: 'root' })
export class AssistantAppContextService implements TellentAssistantAppContextWriter {
  private readonly entries = new BehaviorSubject<readonly ContextEntry[]>([]);

  public readonly context$: Observable<TellentAssistantAppContext> = this.entries.pipe(
    map(entries => ({ entities: toEntities(entries) })),
    distinctUntilChanged(
      (a, b) =>
        a.entities.length === b.entities.length && a.entities.every((e, i) => e === b.entities[i]),
    ),
  );

  public push(entity: AppContextEntity, priority: TellentAssistantAppContextPriority): () => void {
    const entry: ContextEntry = { entity, priority };
    this.entries.next([...this.entries.value, entry]);
    return () => this.entries.next(this.entries.value.filter(e => e !== entry));
  }
}

function toEntities(entries: readonly ContextEntry[]): readonly AppContextEntity[] {
  const sorted = [...entries].sort(
    (a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority],
  );
  const byKey = new Map<string, AppContextEntity>();
  for (const { entity } of sorted) {
    byKey.delete(entityKey(entity));
    byKey.set(entityKey(entity), entity);
  }
  return [...byKey.values()];
}
